import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Plus, ShieldAlert, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import {
  ESCOPOS,
  NAV_KEYS,
  NAV_LABELS,
  PERM_KEYS,
  PERM_LABELS,
  useAtualizarPerfil,
  useCriarPerfil,
  usePerfis,
  useRemoverPerfil,
  type Perfil,
} from "@/api/perfis";
import { useAlteracoesPendentes } from "@/api/profissionais";
import { TopBar } from "@/components/TopBar";
import { RevisaoImpacto } from "@/components/RevisaoImpacto";
import { UsuariosAcessos } from "@/components/UsuariosAcessos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { can, useRole } from "@/lib/use-role";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/perfis")({
  component: PerfisPage,
});

type Aba = "perfis" | "acessos" | "impacto";

function PerfisPage() {
  const role = useRole();
  const perfis = usePerfis();
  const criar = useCriarPerfil();
  const podeEditar = can(role, "editarParametros");
  const podeGerirUsuarios = can(role, "gerenciarUsuarios");

  const [aba, setAba] = useState<Aba>("perfis");
  const revisoes = useAlteracoesPendentes();
  const pendentes = revisoes.data?.reduce((s, a) => s + a.pendentes, 0) ?? 0;
  const [nome, setNome] = useState("");
  const [vinculo, setVinculo] = useState<"INTERNO" | "EXTERNO">("INTERNO");

  return (
    <>
      <TopBar
        title="Perfis e acessos"
        subtitle={`${perfis.data?.length ?? 0} perfis configurados · telas, permissões e escopo de visão`}
      />

      <div className="flex-1 space-y-4 overflow-auto p-4">
        <div className="inline-flex overflow-hidden rounded-md border border-border">
          {(
            [
              ["perfis", "Perfis e permissões"],
              ["acessos", "Usuários e acessos"],
              ["impacto", "Revisão de impacto"],
            ] as const
          ).map(([v, rotulo]) => (
            <button
              key={v}
              onClick={() => setAba(v)}
              disabled={v === "acessos" && !podeGerirUsuarios}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium disabled:opacity-40",
                aba === v
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {rotulo}
              {v === "impacto" && pendentes > 0 && (
                <span className="rounded-full bg-health-yellow/20 px-1.5 text-[10px] font-semibold text-health-yellow">
                  {pendentes}
                </span>
              )}
            </button>
          ))}
        </div>

        {aba === "acessos" ? (
          <UsuariosAcessos />
        ) : aba === "impacto" ? (
          <RevisaoImpacto />
        ) : (
          <>
            {!podeEditar && (
              <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-3 text-[11px] text-muted-foreground">
                <ShieldAlert className="h-4 w-4 text-health-yellow" /> Seu perfil não pode alterar
                permissões — visualização somente leitura.
              </div>
            )}

            {podeEditar && (
              <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-card p-3">
                <div className="min-w-56 flex-1">
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    Novo perfil
                  </div>
                  <Input
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Ex: Coordenador Externo"
                    className="h-8 text-xs"
                  />
                </div>
                <select
                  value={vinculo}
                  onChange={(e) => setVinculo(e.target.value as "INTERNO" | "EXTERNO")}
                  className="h-8 rounded-md border border-border bg-background px-2 text-xs text-foreground"
                >
                  <option value="INTERNO">Interno</option>
                  <option value="EXTERNO">Externo</option>
                </select>
                <Button
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                  disabled={criar.isPending}
                  onClick={() => {
                    if (!nome.trim()) return;
                    criar.mutate(
                      { nome: nome.trim(), vinculo },
                      {
                        onSuccess: () => {
                          setNome("");
                          toast.success("Perfil criado — nasce sem acesso, configure abaixo.");
                        },
                        onError: (e) =>
                          toast.error(e instanceof Error ? e.message : "Falha ao criar"),
                      },
                    );
                  }}
                >
                  <Plus className="h-3.5 w-3.5" /> Criar perfil
                </Button>
              </div>
            )}

            {perfis.isLoading && (
              <div className="flex items-center gap-2 p-4 text-[11px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carregando perfis…
              </div>
            )}

            <div className="space-y-3">
              {perfis.data?.map((p) => (
                <CartaoPerfil key={p.id} perfil={p} podeEditar={podeEditar} />
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
}

function CartaoPerfil({ perfil, podeEditar }: { perfil: Perfil; podeEditar: boolean }) {
  const atualizar = useAtualizarPerfil();
  const remover = useRemoverPerfil();

  const alternar = (campo: "telas" | "permissoes", chave: string) => {
    const atual = perfil[campo];
    const proximo = atual.includes(chave) ? atual.filter((x) => x !== chave) : [...atual, chave];
    atualizar.mutate({ id: perfil.id, [campo]: proximo });
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          value={perfil.nome}
          disabled={!podeEditar || perfil.base}
          onChange={(e) => atualizar.mutate({ id: perfil.id, nome: e.target.value })}
          className="h-8 w-56 text-xs font-semibold"
        />
        <span
          className={cn(
            "rounded-md border px-2 py-0.5 text-[10px] font-medium",
            perfil.vinculo === "INTERNO"
              ? "border-primary/30 bg-primary/10 text-primary"
              : "border-health-yellow/30 bg-health-yellow/10 text-health-yellow",
          )}
        >
          {perfil.vinculo === "INTERNO" ? "Interno" : "Externo"}
        </span>
        {perfil.base && (
          <span className="rounded-md bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
            base
          </span>
        )}
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
          <Users className="h-3 w-3" /> {perfil._count.usuarios}
        </span>

        {podeEditar && !perfil.base && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-8 w-8 p-0 text-muted-foreground hover:text-health-red"
            onClick={() =>
              remover.mutate(perfil.id, {
                onError: (e) => toast.error(e instanceof Error ? e.message : "Falha ao remover"),
              })
            }
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Escopo de visão — três eixos independentes (RF-59) */}
      <div className="mt-3">
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Escopo de visão
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {ESCOPOS.map(({ campo, titulo, opcoes }) => (
            <div key={campo} className="rounded-lg border border-border bg-secondary/30 p-2">
              <div className="mb-1 text-[11px] font-medium text-foreground">{titulo}</div>
              <div className="flex flex-wrap gap-1">
                {opcoes.map((o) => (
                  <button
                    key={o.valor}
                    type="button"
                    disabled={!podeEditar}
                    onClick={() =>
                      atualizar.mutate({
                        id: perfil.id,
                        [campo]: o.valor,
                      } as Parameters<typeof atualizar.mutate>[0])
                    }
                    className={cn(
                      "rounded-md border px-2 py-0.5 text-[10px] font-medium",
                      perfil[campo] === o.valor
                        ? "border-primary/40 bg-primary/15 text-primary"
                        : "border-border bg-background text-muted-foreground",
                    )}
                  >
                    {o.rotulo}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">
          O escopo é aplicado no servidor: o que o perfil não pode ver não sai do banco.
        </p>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">Telas</div>
        <div className="flex flex-wrap gap-1.5">
          {NAV_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              disabled={!podeEditar}
              onClick={() => alternar("telas", k)}
              className={cn(
                "rounded-md border px-2 py-0.5 text-[10px] font-medium",
                perfil.telas.includes(k)
                  ? "border-primary/40 bg-primary/15 text-primary"
                  : "border-border bg-secondary/40 text-muted-foreground",
              )}
            >
              {NAV_LABELS[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
          Permissões
        </div>
        <div className="flex flex-wrap gap-1.5">
          {PERM_KEYS.map((k) => (
            <button
              key={k}
              type="button"
              disabled={!podeEditar}
              onClick={() => alternar("permissoes", k)}
              className={cn(
                "rounded-md border px-2 py-0.5 text-[10px] font-medium",
                perfil.permissoes.includes(k)
                  ? "border-health-green/40 bg-health-green/15 text-health-green"
                  : "border-border bg-secondary/40 text-muted-foreground",
              )}
            >
              {PERM_LABELS[k]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
